import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Avatar, CrmToaster, crmToast } from './crmUi'
import { Button } from '../shared/Button'
import { STAGE_MAP, type Deal, type Task } from './crmData'
import {
  addActivity, addTask, archiveDeal, getDealBookings, getDealEmails, getUsers, moveDealStage, restoreDeal, updateDeal, updateTask,
  type Booking, type LeadEmail,
} from './crmApi'
import { ObSummaryTab } from './ObSummaryTab'
import { DealSetup } from './DealSetup'
import { DealPlatform } from './DealPlatform'
import { DealSupport } from './DealSupport'
import { DealImages } from './DealImages'
import { DealEmails } from './DealEmails'
import { DealMeetings } from './DealMeetings'
import { DealWhatsApp } from './DealWhatsApp'
import { DealConvoHistory } from './DealConvoHistory'
import { DealQualification } from './DealQualification'
import { DealUsers } from './DealUsers'
import { LogCallModal } from './LogCallModal'
import { BookingModal } from './BookingModal'
import { LeadKebab } from './LeadKebab'
import { apiGet } from '../../utils/api'

/** Facts-rail accordion (Lee, 3 Nov 2026): every details section folds. */
const Acc: React.FC<{ title: string; defaultOpen?: boolean; badge?: React.ReactNode; children: React.ReactNode }> = ({ title, defaultOpen = true, badge, children }) => {
  const [open, setOpen] = React.useState(defaultOpen)
  return (
    <div className="rounded-xl border border-border bg-surface-secondary/50">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between gap-2 px-3.5 py-2.5 text-left">
        <span className="text-[10px] font-extrabold uppercase tracking-wider text-txt-tertiary">{title}</span>
        <span className="flex items-center gap-1.5">{badge}<span className="text-[11px] text-txt-tertiary">{open ? '▾' : '▸'}</span></span>
      </button>
      {open && <div className="px-3.5 pb-3.5">{children}</div>}
    </div>
  )
}

/**
 * LEAD SCREEN REDESIGN (Lee, 30 Oct 2026 — signed off via the design-sandbox
 * HTML mockup). Replaces the 16-equal-tabs DealDetail page outright:
 *   · journey bar with days-in-stage (wq_crm_stage_history) + Mark Won / Lost
 *   · chrome-style tab strip: Overview + 7 record tabs (same content as the
 *     old tabs — NOTHING becomes unreachable)
 *   · Overview = left facts rail (summary, contacts, qualification) + pinned
 *     NEXT card + composer strip + ONE merged timeline (activities, emails,
 *     meetings, calls, milestones) with count-labelled filter chips. The old
 *     Activity/Emails/Meetings/WhatsApp/Conversations/Tasks tabs live on as
 *     chips; milestones render as prominent tinted bands.
 *   · fixed viewport — only the feed scrolls.
 */

/* ── tiny icons ── */
const I: React.FC<{ d: string; className?: string }> = ({ d, className }) => (
  <svg className={className || 'w-4 h-4'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d={d} /></svg>
)
const flagOf = (cc?: string | null) => {
  const c = String(cc || '').toUpperCase()
  if (c.length !== 2) return ''
  return String.fromCodePoint(...[...c].map((ch) => 0x1f1e6 + ch.charCodeAt(0) - 65))
}

/* ── milestone classification (same taxonomy as the mockup) ── */
type MilestoneKind = 'stage' | 'trial' | 'expired' | 'won' | 'contract' | 'demo' | 'onboarding' | 'churn'
const MILESTONE_STYLE: Record<MilestoneKind, { bg: string; accent: string; icon: string }> = {
  stage: { bg: 'bg-brand-50', accent: 'border-l-brand-500', icon: 'M4 21V4m0 1h12l-2 3 2 3H4' },
  trial: { bg: 'bg-blue-50', accent: 'border-l-blue-500', icon: 'M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09zM12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z' },
  expired: { bg: 'bg-amber-50', accent: 'border-l-amber-500', icon: 'M5 22h14M5 2h14M17 22v-4.172a2 2 0 0 0-.586-1.414L12 12l-4.414 4.414A2 2 0 0 0 7 17.828V22m10-20v4.172a2 2 0 0 1-.586 1.414L12 12 7.586 7.586A2 2 0 0 1 7 6.172V2' },
  won: { bg: 'bg-emerald-50', accent: 'border-l-emerald-500', icon: 'M6 9H4.5a2.5 2.5 0 0 1 0-5H6m12 5h1.5a2.5 2.5 0 0 0 0-5H18M4 22h16M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22m10-7.34V17c0 .55.47.98.97 1.21C19.15 18.75 20 20.24 20 22M18 2H6v7a6 6 0 0 0 12 0V2Z' },
  contract: { bg: 'bg-emerald-50', accent: 'border-l-emerald-600', icon: 'M12 19l7-7 3 3-7 7-3-3zM18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5zM2 2l7.586 7.586' },
  demo: { bg: 'bg-purple-50', accent: 'border-l-purple-500', icon: 'M23 7l-7 5 7 5V7zM14 5H3a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2z' },
  onboarding: { bg: 'bg-teal-50', accent: 'border-l-teal-500', icon: 'M8 2v4m8-4v4M3 10h18M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z' },
  churn: { bg: 'bg-red-50', accent: 'border-l-red-500', icon: 'M22 17l-8.5-8.5-5 5L2 7m20 10h-6m6 0v-6' },
}
const classifyMilestone = (type: string, body: string): MilestoneKind | null => {
  const b = body.toLowerCase()
  if (type === 'stage_change' || b.startsWith('moved to')) {
    if (b.includes('cancelled') || b.includes('closed lost') || b.includes('churn')) return 'churn'
    if (b.includes('successfully signed up') || b.includes('all live')) return 'won'
    return 'stage'
  }
  if (/trial started|signed up for a new free trial|re-signed/.test(b)) return 'trial'
  if (/trial expired|expired —|revived/.test(b)) return 'expired'
  if (/contract (sent|signed|countersigned)|countersigned/.test(b)) return 'contract'
  if (/demo (booked|held)|calendly demo/.test(b)) return 'demo'
  if (/onboarding (started|complete)|go-live|went live|now a paying|went paid|linked to wequote account.*paid/.test(b)) return b.includes('paying') || b.includes('went paid') ? 'won' : 'onboarding'
  return null
}

/* ── merged feed item ── */
interface FeedItem {
  ts: number
  kind: 'milestone' | 'email' | 'call' | 'meeting' | 'note' | 'system' | 'task' | 'whatsapp'
  milestone?: MilestoneKind
  title: string
  body?: string | null
  who?: string | null
  meta?: string | null
  email?: LeadEmail
  booking?: Booking
}

const fmtTime = (ts: number) => new Date(ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
const dayKey = (ts: number) => new Date(ts).toDateString()
const dayLabel = (ts: number) => {
  const d = new Date(ts); const today = new Date(); const yd = new Date(Date.now() - 864e5)
  if (d.toDateString() === today.toDateString()) return 'Today'
  if (d.toDateString() === yd.toDateString()) return 'Yesterday'
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: d.getFullYear() !== today.getFullYear() ? 'numeric' : undefined })
}

type ChipKey = 'all' | 'milestones' | 'emails' | 'calls' | 'meetings' | 'notes' | 'system' | 'whatsapp' | 'chats' | 'tasks'
type CentreView = 'feed' | 'qualification' | 'contacts'

export const LeadScreen: React.FC<{ deal: Deal & Record<string, any>; onChanged: () => void; initialTab?: string }> = ({ deal, onChanged, initialTab }) => {
  const navigate = useNavigate()
  const LEGACY_TAB_MAP: Record<string, string> = {
    lead: 'overview', activity: 'overview', tasks: 'overview', meetings: 'overview', users: 'overview', convo: 'overview',
    whatsapp: 'overview', email: 'overview', setup: 'setup', platform: 'platform', support: 'support', quotes: 'quotes',
    invoices: 'invoices', images: 'images', onboarding: 'onboarding', overview: 'overview',
  }
  const [tab, setTab] = useState<string>(LEGACY_TAB_MAP[initialTab || 'overview'] || 'overview')
  const [users, setUsers] = useState<Array<{ id: string; name: string }>>([])
  useEffect(() => { getUsers().then((u: any) => setUsers(u.users || u)).catch(() => {}) }, [])

  /* ── journey bar from stage history ── */
  const journey = useMemo(() => {
    const hist: Array<{ from: string | null; to: string; at: string | null }> = (deal as any).stageHistory || []
    const steps: Array<{ stage: string; enteredAt: number | null }> = []
    if (hist.length && hist[0].from) steps.push({ stage: hist[0].from, enteredAt: null })
    for (const h of hist) if (!steps.length || steps[steps.length - 1].stage !== h.to) steps.push({ stage: h.to, enteredAt: h.at ? new Date(h.at).getTime() : null })
    if (!steps.length || steps[steps.length - 1].stage !== deal.stage) steps.push({ stage: deal.stage, enteredAt: null })
    return steps.map((s, i) => {
      const next = steps[i + 1]
      const days = s.enteredAt != null ? Math.max(0, Math.round(((next?.enteredAt ?? Date.now()) - s.enteredAt) / 864e5)) : null
      return { ...s, days, current: i === steps.length - 1 }
    }).slice(-6) // the last 6 hops — older history lives in the timeline
  }, [deal])

  const markWon = () => {
    if (!window.confirm(`Mark ${deal.name} as WON (Successfully Signed Up)?`)) return
    moveDealStage(deal.id, 'successfully_signed_up').then(() => { crmToast('Marked won 🎉', 'success'); onChanged() }).catch((e: any) => crmToast(e?.message || 'Failed', 'error'))
  }
  const markLost = () => {
    const reason = window.prompt('Mark as LOST — reason? (required)')
    if (!reason || !reason.trim()) return
    moveDealStage(deal.id, 'closed_lost', reason.trim()).then(() => { crmToast('Marked lost', 'success'); onChanged() }).catch((e: any) => crmToast(e?.message || 'Failed', 'error'))
  }

  const TABS: Array<{ key: string; label: string; count?: string | number | null }> = [
    { key: 'overview', label: 'Overview' },
    { key: 'onboarding', label: 'Onboarding' },
    { key: 'quotes', label: 'Quotes', count: (deal.quotes || []).length || null },
    { key: 'invoices', label: 'Invoices' },
    { key: 'setup', label: 'Setup' },
    { key: 'platform', label: 'Platform' },
    { key: 'support', label: 'Support' },
    { key: 'images', label: 'Images' },
  ]

  const terminal = ['successfully_signed_up', 'closed_lost', 'cancelled_subscription'].includes(deal.stage)

  return (
    <div className="h-full flex flex-col overflow-hidden bg-surface-secondary">
      <CrmToaster />
      {/* ── header ── */}
      <div className="shrink-0 bg-surface border-b border-border px-5 pt-3 pb-0">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="w-8 h-8 rounded-lg border border-border text-txt-secondary hover:bg-surface-hover flex items-center justify-center shrink-0" title="Back">
            <I d="M19 12H5m7-7-7 7 7 7" />
          </button>
          <Avatar name={deal.name} size={38} logoUrl={deal.logoUrl || undefined} />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 min-w-0">
              <h1 className="text-[18px] font-extrabold text-txt-primary truncate">{deal.name}</h1>
              {(deal.flags || []).map((f: any) => (
                <span key={f.id} className="px-1.5 py-0.5 rounded-full text-[9px] font-bold text-white shrink-0" style={{ background: f.color }}>{f.name}</span>
              ))}
            </div>
            <p className="text-[12px] text-txt-tertiary truncate">
              {deal.contact || '—'}{deal.country ? <> · {flagOf(deal.country)} {deal.country}</> : null} · {STAGE_MAP[deal.stage]?.label || deal.stage}
            </p>
          </div>
          <select
            value={deal.ownerId || ''}
            onChange={(e) => updateDeal(deal.id, { ownerId: e.target.value || null }).then(() => { crmToast('Owner updated', 'success'); onChanged() }).catch(() => crmToast('Not saved', 'error'))}
            className="rounded-lg border border-border bg-surface px-2 py-1.5 text-[12px] text-txt-primary focus:outline-none focus:border-brand-400"
            title="Lead owner"
          >
            <option value="">No owner</option>
            {users.map((u) => <option key={u.id} value={String(u.id)}>{u.name}</option>)}
          </select>
          <LeadKebab dealId={deal.id} dealName={deal.name} email={deal.contactRecord?.email || null} phone={deal.contactPhone || null} onChanged={onChanged} />
        </div>

        {/* ── cancellation banners (Lee, 30 Oct 2026) ── */}
        {(deal as any).cancelEffectiveAt && !terminal ? (
          <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-2.5 flex items-center gap-3 flex-wrap">
            <p className="text-[12.5px] font-bold text-amber-800 flex-1 min-w-[260px]">
              ⏳ CANCELLATION PENDING — leaves on {new Date((deal as any).cancelEffectiveAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
              {(deal as any).cancelRequestReason ? <span className="font-semibold"> · “{(deal as any).cancelRequestReason}”</span> : null}
            </p>
            <Button size="sm" variant="secondary" onClick={() => { import('./crmApi').then((m) => m.dismissCancelRequest(deal.id)).then(() => { crmToast('Banner cleared', 'success'); onChanged() }) }}>Dismiss</Button>
          </div>
        ) : (deal as any).cancelRequestedAt && !terminal ? (
          <div className="mt-3 rounded-xl border border-red-300 bg-red-50 px-4 py-2.5 flex items-center gap-3 flex-wrap">
            <p className="text-[12.5px] font-bold text-red-700 flex-1 min-w-[260px]">
              🚨 CANCELLATION REQUESTED — {new Date((deal as any).cancelRequestedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
              {(deal as any).cancelRequestReason ? <span className="font-semibold"> · “{(deal as any).cancelRequestReason}”</span> : null}
              <span className="font-medium text-red-600"> — call them before it becomes a cancellation.</span>
            </p>
            <Button size="sm" onClick={() => setTab('overview')}>Open activity</Button>
            <Button size="sm" variant="secondary" onClick={() => { if (window.confirm('Clear the cancellation-request banner? (Use after the save call is handled.)')) import('./crmApi').then((m) => m.dismissCancelRequest(deal.id)).then(() => { crmToast('Banner cleared', 'success'); onChanged() }) }}>Dismiss</Button>
          </div>
        ) : null}

        {/* ── journey bar + won/lost ── */}
        <div className="flex items-center gap-2 mt-3 pb-3">
          <div className="flex-1 min-w-0 flex items-stretch gap-1 overflow-x-auto">
            {journey.map((s, i) => (
              <div key={i}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11.5px] font-bold whitespace-nowrap ${s.current ? 'bg-brand-600 text-white' : 'bg-surface-tertiary text-txt-secondary'}`}
                style={{ clipPath: journey.length > 1 ? 'polygon(0 0, calc(100% - 8px) 0, 100% 50%, calc(100% - 8px) 100%, 0 100%, 8px 50%)' : undefined, paddingLeft: 16, paddingRight: 18 }}
                title={STAGE_MAP[s.stage]?.label || s.stage}
              >
                {s.days != null && <span className={`text-[10px] font-extrabold ${s.current ? 'text-white/80' : 'text-txt-tertiary'}`}>{s.current ? `${s.days}d · now` : `${s.days}d`}</span>}
                {STAGE_MAP[s.stage]?.label || s.stage}
              </div>
            ))}
          </div>
          {!terminal && (
            <>
              <button onClick={markWon} className="shrink-0 px-3.5 py-1.5 rounded-lg text-[12px] font-bold text-white bg-emerald-600 hover:bg-emerald-700">✓ Mark Won</button>
              <button onClick={markLost} className="shrink-0 px-3.5 py-1.5 rounded-lg text-[12px] font-bold text-red-600 border border-red-300 bg-surface hover:bg-red-50">✕ Mark Lost</button>
            </>
          )}
          {/* ARCHIVE SURFACED (Lee, 2 Nov 2026): obvious button next to
              Won/Lost — was kebab-only since the 715 redesign. */}
          {deal.archivedAt ? (
            <button onClick={() => { if (window.confirm(`Unarchive ${deal.name}? It returns to every list, board and metric.`)) restoreDeal(deal.id).then(() => { crmToast('Lead restored', 'success'); onChanged() }).catch(() => crmToast('Failed', 'error')) }}
              className="shrink-0 px-3.5 py-1.5 rounded-lg text-[12px] font-bold text-txt-secondary border border-border bg-surface hover:bg-surface-hover">↩ Unarchive</button>
          ) : (
            <button onClick={() => { if (window.confirm(`Archive ${deal.name}? Hidden from every list, board and metric — restorable any time.`)) archiveDeal(deal.id).then(() => { crmToast('Lead archived', 'success'); onChanged() }).catch(() => crmToast('Failed', 'error')) }}
              className="shrink-0 px-3.5 py-1.5 rounded-lg text-[12px] font-bold text-txt-secondary border border-border bg-surface hover:bg-surface-hover" title="Hidden from every list, board and metric — restorable any time">Archive</button>
          )}
        </div>

        {/* ── chrome-style tab strip ── */}
        <div className="flex items-end gap-0.5 -mb-px">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-3.5 py-2 rounded-t-xl text-[12px] font-bold border border-b-0 transition-colors ${tab === t.key
                ? 'bg-surface-secondary border-border text-brand-700 relative z-10'
                : 'bg-surface-tertiary/60 border-transparent text-txt-tertiary hover:text-txt-primary hover:bg-surface-tertiary'}`}>
              {t.label}
              {t.count != null && <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[9.5px] bg-surface text-txt-secondary border border-border">{t.count}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* ── tab bodies — the single scroll region per tab ── */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {tab === 'overview' && <LeadOverview deal={deal} onChanged={onChanged} />}
        {tab !== 'overview' && (
          <div className="h-full overflow-y-auto">
            <div className={`mx-auto p-5 ${tab === 'onboarding' ? 'max-w-[1100px]' : 'max-w-[860px]'}`}>
              {tab === 'onboarding' && <ObSummaryTab dealId={deal.id} />}
              {tab === 'quotes' && <QuotesPanel deal={deal} />}
              {tab === 'invoices' && <LazyPanel name="invoices" deal={deal} onChanged={onChanged} />}
              {tab === 'setup' && <DealSetup dealId={deal.id} />}
              {tab === 'platform' && <DealPlatform deal={deal as any} />}
              {tab === 'support' && <DealSupport deal={deal as any} />}
              {tab === 'images' && <DealImages dealId={deal.id} hasWequoteLogo={!!(deal as any).logoFromWequote} onChanged={onChanged} />}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

/* Invoices is a default-export-less inline import in the old screen; isolate it. */
const LazyPanel: React.FC<{ name: 'invoices'; deal: Deal; onChanged: () => void }> = ({ deal }) => {
  const [C, setC] = useState<React.ComponentType<any> | null>(null)
  useEffect(() => { import('./DealInvoices').then((m: any) => setC(() => m.DealInvoices || m.default)).catch(() => {}) }, [])
  return C ? <C dealId={deal.id} /> : <p className="text-[12px] text-txt-tertiary">Loading…</p>
}

/* ── Quotes (extracted from the old tab, identical data) ── */
const QUOTE_STATUS: Record<string, string> = {
  draft: 'bg-surface-tertiary text-txt-secondary', sent: 'bg-blue-50 text-blue-700',
  accepted: 'bg-emerald-50 text-emerald-700', rejected: 'bg-red-50 text-red-600',
}
const QuotesPanel: React.FC<{ deal: Deal & Record<string, any> }> = ({ deal }) => (
  <div className="flex flex-col gap-2.5">
    {(deal.quotes || []).length === 0 && <p className="text-xs text-txt-tertiary">No quotes yet</p>}
    {(deal.quotes || []).map((q: any) => (
      <div key={q.id} className="rounded-xl border border-border bg-surface p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-bold text-txt-primary">{q.num}</span>
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${QUOTE_STATUS[q.status] || 'bg-surface-tertiary text-txt-secondary'}`}>{q.status}</span>
          <span className="text-sm font-extrabold text-brand-600 ml-auto">£{Number(q.total).toLocaleString('en-GB')}</span>
        </div>
        {(q.lines || []).map((l: any, i: number) => (
          <div key={i} className="flex justify-between text-[11px] text-txt-secondary py-0.5">
            <span>{l.name} ×{l.qty}</span>
            <span>£{(l.price * l.qty).toLocaleString('en-GB')}</span>
          </div>
        ))}
      </div>
    ))}
  </div>
)

/* ══════════════════════ OVERVIEW ══════════════════════ */
const LeadOverview: React.FC<{ deal: Deal & Record<string, any>; onChanged: () => void }> = ({ deal, onChanged }) => {
  const [chip, setChip] = useState<ChipKey>('all')
  const [centre, setCentre] = useState<CentreView>('feed')
  const [emails, setEmails] = useState<LeadEmail[]>([])
  const [bookings, setBookings] = useState<Booking[]>([])
  const [note, setNote] = useState('')
  const [callOpen, setCallOpen] = useState(false)
  const [bookOpen, setBookOpen] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)
  // Website form submissions (Lee, 3 Nov 2026).
  const [formSubs, setFormSubs] = useState<Array<{ id: string; kind: string; label: string; email: string | null; name: string | null; company: string | null; phone: string | null; country: string | null; receivedAt: string | null; answers: Record<string, unknown> }>>([])
  useEffect(() => { apiGet<{ submissions: any[] }>(`/crm/deals/${deal.id}/form-submissions`).then((r) => setFormSubs(r.submissions || [])).catch(() => setFormSubs([])) }, [deal.id])
  const feedRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    getDealEmails(deal.id).then(setEmails).catch(() => setEmails([]))
    getDealBookings(deal.id).then(setBookings).catch(() => setBookings([]))
  }, [deal.id])

  /* merged feed */
  const items = useMemo<FeedItem[]>(() => {
    const out: FeedItem[] = []
    for (const a of (deal.activities || []) as any[]) {
      const ts = a.occurredAt ? new Date(a.occurredAt).getTime() : Date.now() - (a.at || 0) * 864e5
      const mk = classifyMilestone(a.type, String(a.body || ''))
      if (mk) { out.push({ ts, kind: 'milestone', milestone: mk, title: a.body, who: a.who }); continue }
      if (a.type === 'call') { out.push({ ts, kind: 'call', title: a.body, who: a.who }); continue }
      if (a.type === 'note') { out.push({ ts, kind: 'note', title: a.body, who: a.who }); continue }
      out.push({ ts, kind: 'system', title: a.body, who: a.who })
    }
    for (const e of emails) {
      out.push({ ts: new Date(e.sentAt).getTime(), kind: 'email', title: e.subject || '(no subject)', body: e.snippet, who: e.direction === 'in' ? e.from : 'us', meta: e.direction, email: e })
    }
    for (const b of bookings) {
      if (!b.startAt) continue
      out.push({ ts: new Date(b.startAt).getTime(), kind: 'meeting', title: b.title, meta: b.status, booking: b })
    }
    return out.sort((a, b) => b.ts - a.ts)
  }, [deal.activities, emails, bookings])

  const counts = useMemo(() => ({
    all: items.length,
    milestones: items.filter((i) => i.kind === 'milestone').length,
    emails: items.filter((i) => i.kind === 'email').length,
    calls: items.filter((i) => i.kind === 'call').length,
    meetings: items.filter((i) => i.kind === 'meeting').length,
    notes: items.filter((i) => i.kind === 'note').length,
    system: items.filter((i) => i.kind === 'system').length,
    tasks: (deal.tasks || []).filter((t: Task) => !t.done).length,
  }), [items, deal.tasks])

  const CHIPS: Array<{ key: ChipKey; label: string; count?: number }> = [
    { key: 'all', label: 'All activity', count: counts.all },
    { key: 'milestones', label: 'Milestones', count: counts.milestones },
    { key: 'emails', label: 'Emails', count: counts.emails },
    { key: 'calls', label: 'Calls', count: counts.calls },
    { key: 'meetings', label: 'Meetings', count: counts.meetings },
    { key: 'tasks', label: 'Tasks', count: counts.tasks },
    { key: 'notes', label: 'Notes', count: counts.notes },
    { key: 'whatsapp', label: 'WhatsApp' },
    { key: 'chats', label: 'Conversations' },
    { key: 'system', label: 'System', count: counts.system },
  ]

  const filtered = useMemo(() => {
    if (chip === 'all') return items
    if (chip === 'milestones') return items.filter((i) => i.kind === 'milestone')
    if (chip === 'emails') return items.filter((i) => i.kind === 'email')
    if (chip === 'calls') return items.filter((i) => i.kind === 'call')
    if (chip === 'meetings') return items.filter((i) => i.kind === 'meeting')
    if (chip === 'notes') return items.filter((i) => i.kind === 'note')
    if (chip === 'system') return items.filter((i) => i.kind === 'system')
    return items
  }, [items, chip])

  /* next = the flagged next task, else earliest open task, else next planned booking */
  const next = useMemo(() => {
    const open = ((deal.tasks || []) as Task[]).filter((t) => !t.done)
    const task = open.find((t) => t.isNext) || open.sort((a, b) => (a.due ?? 999) - (b.due ?? 999))[0]
    const booking = bookings.filter((b) => b.status === 'planned' && b.startAt && new Date(b.startAt).getTime() > Date.now())
      .sort((a, b) => new Date(a.startAt!).getTime() - new Date(b.startAt!).getTime())[0]
    return { task, booking }
  }, [deal.tasks, bookings])

  const saveNote = () => {
    const v = note.trim()
    if (!v) return
    addActivity(deal.id, { type: 'note', body: v }).then(() => { setNote(''); crmToast('Note added', 'success'); onChanged() }).catch(() => crmToast('Not saved', 'error'))
  }

  const componentChip = chip === 'whatsapp' || chip === 'chats' || chip === 'tasks' || chip === 'emails' || chip === 'meetings'

  return (
    <div className="h-full flex min-h-0">
      {/* ── left facts rail ── */}
      <div className="w-[320px] shrink-0 h-full overflow-y-auto border-r border-border bg-surface p-3.5 space-y-3 [scrollbar-width:thin]">
        <Acc title="Deal & commercials">
          <div className="flex items-baseline justify-between">
            <p className="text-[22px] font-extrabold text-txt-primary">£{Number(deal.mrr || 0).toLocaleString('en-GB')}<span className="text-[11px] font-semibold text-txt-tertiary">/mo MRR</span></p>
          </div>
          <div className="mt-2 space-y-1.5 text-[12px]">
            <Row k="Plan" v={deal.plan || '—'} />
            <Row k="Country" v={`${flagOf(deal.country)} ${deal.country || '—'}`} />
            <Row k="Source" v={deal.source || 'not set'} />
            <Row k="Score" v={`${deal.score ?? '—'}`} />
            {deal.trialStartedDays != null && <Row k="Trial" v={`Day ${deal.trialStartedDays}${deal.trialEndsDays != null ? ` · ${deal.trialEndsDays}d left` : ''}`} />}
            {deal.demoAt && <Row k="Demo" v={new Date(deal.demoAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })} />}
          </div>
          <button onClick={() => setMoreOpen(!moreOpen)} className="mt-2 text-[11px] font-bold text-brand-600 hover:underline">{moreOpen ? 'Less' : 'More details'} {moreOpen ? '↑' : '→'}</button>
          {moreOpen && (
            <div className="mt-2 space-y-1.5 text-[12px] border-t border-border/60 pt-2">
              <Row k="Industry" v={(deal as any).industry || '—'} />
              <Row k="Size" v={(deal as any).companySizeBand || '—'} />
              <Row k="Phone" v={(deal as any).companyPhone || deal.contactPhone || '—'} />
              <Row k="Account" v={deal.wequoteAccountId ? `#${deal.wequoteAccountId}` : 'not linked'} />
              {deal.driveFolderUrl && <a href={deal.driveFolderUrl} target="_blank" rel="noreferrer" className="block text-[11px] font-bold text-brand-600 hover:underline">Drive folder →</a>}
              {(deal as any).companyNotes && <p className="text-[11px] text-txt-secondary whitespace-pre-wrap">{(deal as any).companyNotes}</p>}
              {(deal.tags || []).length > 0 && <p className="text-[11px] text-txt-tertiary">Tags: {(deal.tags || []).join(' · ')}</p>}
            </div>
          )}
        </Acc>

        {/* contacts */}
        <Acc title="Contacts" badge={<button onClick={(e) => { e.stopPropagation(); setCentre(centre === 'contacts' ? 'feed' : 'contacts') }} className="text-[11px] font-bold text-brand-600 hover:underline">{centre === 'contacts' ? 'Close' : 'Manage →'}</button>}>
          {deal.contactRecord ? (
            <div className="text-[12px]">
              <p className="font-bold text-txt-primary">{deal.contactRecord.firstName} {deal.contactRecord.lastName} <span className="font-normal text-[10px] text-txt-tertiary">· primary</span></p>
              {deal.contactRecord.email && <p className="text-txt-secondary truncate">{deal.contactRecord.email}</p>}
              {(deal.contactRecord.phone || deal.contactPhone) && (
                <p className="text-txt-secondary flex items-center gap-2">{deal.contactRecord.phone || deal.contactPhone}
                  <a href={`tel:${deal.contactRecord.phone || deal.contactPhone}`} className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[10px] font-bold">Call</a>
                </p>
              )}
            </div>
          ) : <p className="text-[12px] text-txt-tertiary">No primary contact</p>}
          {(deal.secondaryContacts || []).length > 0 && <p className="mt-1.5 text-[11px] text-txt-tertiary">+ {(deal.secondaryContacts || []).length} more — Manage</p>}
        </Acc>

        {/* form submissions (website forms — Lee, 3 Nov 2026) */}
        {formSubs.length > 0 && (
          <Acc title="Form submissions" badge={deal.formStatus ? <span className="px-1.5 py-0.5 rounded text-[8.5px] font-extrabold text-white" style={{ background: deal.formStatus === 'trial_requested' ? '#7C3AED' : '#D97706' }}>{deal.formStatus === 'trial_requested' ? 'REQUESTED' : 'BOOKING PENDING'}</span> : undefined}>
            <div className="space-y-2.5">
              {formSubs.map((f) => (
                <div key={f.id} className="rounded-lg border border-border/70 bg-surface p-2.5">
                  <p className="text-[11.5px] font-bold text-txt-primary">{f.label}
                    <span className="font-normal text-[10px] text-txt-tertiary"> · {f.receivedAt ? new Date(f.receivedAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}</span>
                  </p>
                  <div className="mt-1 space-y-0.5 text-[11.5px]">
                    {([['Name', f.name], ['Email', f.email], ['Company', f.company], ['Phone', f.phone], ['Country', f.country]] as Array<[string, string | null]>).filter(([, v]) => v).map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-2"><span className="text-txt-tertiary">{k}</span><span className="text-txt-primary text-right break-all">{v}</span></div>
                    ))}
                    {Object.entries(f.answers || {}).map(([k, v]) => (
                      <div key={k} className="flex justify-between gap-2"><span className="text-txt-tertiary capitalize">{k.replace(/[_-]+/g, ' ')}</span><span className="text-txt-primary text-right whitespace-pre-wrap break-words max-w-[170px]">{String(v)}</span></div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Acc>
        )}

        {/* qualification */}
        <Acc title="Qualification" badge={<button onClick={(e) => { e.stopPropagation(); setCentre(centre === 'qualification' ? 'feed' : 'qualification') }} className="text-[11px] font-bold text-brand-600 hover:underline">{centre === 'qualification' ? 'Close' : 'Open →'}</button>}>
          <div className="flex items-center gap-3">
            <div className="relative w-12 h-12 shrink-0">
              <svg viewBox="0 0 36 36" className="w-12 h-12 -rotate-90">
                <circle cx="18" cy="18" r="15.9" fill="none" stroke="var(--color-border)" strokeWidth="3.5" />
                <circle cx="18" cy="18" r="15.9" fill="none" stroke={deal.qualPct != null && deal.qualPct >= 67 ? '#059669' : '#f59e0b'} strokeWidth="3.5" strokeDasharray={`${deal.qualPct ?? 0} 100`} strokeLinecap="round" />
              </svg>
              <span className="absolute inset-0 flex items-center justify-center text-[11px] font-extrabold text-txt-primary">{deal.qualPct != null ? `${deal.qualPct}%` : '—'}</span>
            </div>
            <p className="text-[11.5px] text-txt-secondary">{deal.qualificationStatus === 'qualified' ? 'Qualified' : deal.qualificationStatus === 'nurture' ? 'Nurture' : deal.qualificationStatus === 'disqualified' ? 'Disqualified' : 'Working'}{deal.leadGrade ? ` · grade ${deal.leadGrade}` : ''}</p>
          </div>
        </Acc>
      </div>

      {/* ── centre ── */}
      <div className="flex-1 min-w-0 h-full flex flex-col min-h-0">
        {centre === 'qualification' && (
          <div className="flex-1 min-h-0 overflow-y-auto p-5">
            <button onClick={() => setCentre('feed')} className="mb-3 text-[12px] font-bold text-brand-600 hover:underline">← Back to activity</button>
            <DealQualification dealId={deal.id} onChanged={() => onChanged()} />
          </div>
        )}
        {centre === 'contacts' && (
          <div className="flex-1 min-h-0 overflow-y-auto p-5">
            <button onClick={() => setCentre('feed')} className="mb-3 text-[12px] font-bold text-brand-600 hover:underline">← Back to activity</button>
            <DealUsers dealId={deal.id} dealName={deal.name} />
          </div>
        )}
        {centre === 'feed' && (
          <>
            {/* pinned NEXT */}
            <div className="shrink-0 px-5 pt-4">
              {next.task || next.booking ? (
                <div className="rounded-xl border border-amber-300 bg-amber-50/60 px-4 py-2.5 flex items-center gap-3 flex-wrap">
                  <span className="text-[9.5px] font-extrabold uppercase tracking-wider text-amber-700 shrink-0">Next</span>
                  {next.booking && new Date(next.booking.startAt!).getTime() < (next.task ? Date.now() + (next.task.due ?? 999) * 864e5 : Infinity) ? (
                    <p className="text-[12.5px] font-bold text-txt-primary flex-1 min-w-[220px]">
                      {next.booking.title}
                      <span className="font-medium text-txt-secondary"> · {new Date(next.booking.startAt!).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}{next.booking.meetUrl ? ' · Meet' : ''}</span>
                    </p>
                  ) : next.task ? (
                    <>
                      <p className="text-[12.5px] font-bold text-txt-primary flex-1 min-w-[220px]">
                        {next.task.title}
                        <span className="font-medium text-txt-secondary">{next.task.due != null ? ` · ${next.task.due < 0 ? `${-next.task.due}d overdue` : next.task.due === 0 ? 'due today' : `due in ${next.task.due}d`}` : ''}</span>
                      </p>
                      <button onClick={() => updateTask(next.task!.id, { done: true }).then(() => { crmToast('Done ✓', 'success'); onChanged() })} className="px-2.5 py-1 rounded-lg text-[11px] font-bold bg-emerald-600 text-white hover:bg-emerald-700 shrink-0">Mark done</button>
                    </>
                  ) : null}
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-border bg-surface px-4 py-2.5 flex items-center gap-3">
                  <p className="text-[12px] text-txt-tertiary flex-1">No next action scheduled — this lead is drifting.</p>
                  <Button size="sm" onClick={() => setBookOpen(true)}>Book meeting</Button>
                  <Button size="sm" variant="secondary" onClick={() => { const t = window.prompt('Task title'); if (t?.trim()) addTask(deal.id, { title: t.trim(), dueInDays: 1 }).then(() => { crmToast('Task added', 'success'); onChanged() }) }}>Add task</Button>
                </div>
              )}
            </div>

            {/* composer strip */}
            <div className="shrink-0 px-5 pt-3 flex items-center gap-2 flex-wrap">
              <Button size="sm" variant="secondary" onClick={() => setCallOpen(true)}>📞 Log call</Button>
              <Button size="sm" variant="secondary" onClick={() => setBookOpen(true)}>📅 Book meeting</Button>
              <Button size="sm" variant="secondary" onClick={() => setChip('emails')}>✉️ Email</Button>
              <Button size="sm" variant="secondary" onClick={() => setChip('whatsapp')}>💬 WhatsApp</Button>
              <input
                value={note} onChange={(e) => setNote(e.target.value)}
                onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') saveNote() }}
                placeholder="Write a quick note… (⌘↵ to save)"
                className="flex-1 min-w-[200px] rounded-lg border border-border bg-surface px-3 py-1.5 text-[12px] text-txt-primary focus:outline-none focus:border-brand-400"
              />
              {note.trim() && <Button size="sm" onClick={saveNote}>Save note</Button>}
            </div>

            {/* filter chips */}
            <div className="shrink-0 px-5 pt-3 pb-2 flex items-center gap-1.5 flex-wrap border-b border-border/70">
              {CHIPS.map((c) => (
                <button key={c.key} onClick={() => setChip(c.key)}
                  className={`px-2.5 py-1 rounded-full text-[11px] font-bold border ${chip === c.key ? 'bg-txt-primary text-surface border-txt-primary' : 'bg-surface text-txt-secondary border-border hover:bg-surface-hover'}`}>
                  {c.label}{c.count != null && c.count > 0 ? <span className="ml-1 opacity-60">{c.count}</span> : null}
                </button>
              ))}
            </div>

            {/* THE one scrolling region */}
            <div ref={feedRef} className="flex-1 min-h-0 overflow-y-auto px-5 py-3 [scrollbar-width:thin]">
              {componentChip ? (
                <div className="max-w-[880px]">
                  {chip === 'emails' && <DealEmails dealId={deal.id} />}
                  {chip === 'meetings' && <DealMeetings dealId={deal.id} dealName={deal.name} />}
                  {chip === 'whatsapp' && <DealWhatsApp dealId={deal.id} dealName={deal.name} />}
                  {chip === 'chats' && <DealConvoHistory dealId={deal.id} />}
                  {chip === 'tasks' && <TasksPanel deal={deal} onChanged={onChanged} />}
                </div>
              ) : (
                <Feed items={filtered} openEmail={() => setChip('emails')} openMeetings={() => setChip('meetings')} />
              )}
            </div>
          </>
        )}
      </div>

      {callOpen && <LogCallModal dealId={deal.id} dealName={deal.name} onClose={() => setCallOpen(false)} onLogged={() => { setCallOpen(false); onChanged() }} />}
      {bookOpen && <BookingModal dealId={deal.id} dealName={deal.name} initialType="meeting" onClose={() => setBookOpen(false)} onSaved={() => { setBookOpen(false); onChanged() }} />}
    </div>
  )
}

const Row: React.FC<{ k: string; v: React.ReactNode }> = ({ k, v }) => (
  <div className="flex items-baseline justify-between gap-3">
    <span className="text-txt-tertiary">{k}</span>
    <span className="font-semibold text-txt-primary text-right truncate">{v}</span>
  </div>
)

/* ── merged feed renderer ── */
const KIND_ICON: Record<string, { d: string; cls: string }> = {
  email: { d: 'M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2zm18 2-10 7L2 6', cls: 'bg-blue-50 text-blue-600 border-blue-200' },
  call: { d: 'M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z', cls: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
  meeting: { d: 'M23 7l-7 5 7 5V7zM14 5H3a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2z', cls: 'bg-purple-50 text-purple-600 border-purple-200' },
  note: { d: 'M12 20h9M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z', cls: 'bg-amber-50 text-amber-600 border-amber-200' },
  whatsapp: { d: 'M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z', cls: 'bg-green-50 text-green-600 border-green-200' },
}
const Feed: React.FC<{ items: FeedItem[]; openEmail: () => void; openMeetings: () => void }> = ({ items, openEmail, openMeetings }) => {
  if (!items.length) return <p className="text-[12px] text-txt-tertiary py-6 text-center">Nothing here yet.</p>
  let lastDay = ''
  return (
    <div className="max-w-[880px] space-y-1.5 pb-6">
      {items.map((it, idx) => {
        const dk = dayKey(it.ts)
        const header = dk !== lastDay ? <p key={`d${idx}`} className="pt-3 pb-1 text-[10px] font-extrabold uppercase tracking-wider text-txt-tertiary border-b border-border/50 mb-1.5">{dayLabel(it.ts)}</p> : null
        lastDay = dk
        return (
          <React.Fragment key={idx}>
            {header}
            {it.kind === 'milestone' ? (
              <div className={`rounded-xl border border-border/60 border-l-4 ${MILESTONE_STYLE[it.milestone!].accent} ${MILESTONE_STYLE[it.milestone!].bg} px-3.5 py-2.5 flex items-center gap-3`}>
                <svg className="w-4 h-4 shrink-0 text-txt-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d={MILESTONE_STYLE[it.milestone!].icon} /></svg>
                <p className="text-[12.5px] font-bold text-txt-primary flex-1 min-w-0">{it.title}</p>
                <span className="text-[10.5px] text-txt-tertiary shrink-0">{it.who || 'System'} · {fmtTime(it.ts)}</span>
              </div>
            ) : it.kind === 'system' ? (
              <div className="flex items-center gap-2 px-1 py-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-border-strong shrink-0" />
                <p className="text-[11px] text-txt-tertiary flex-1 min-w-0 truncate" title={it.title}>{it.title}</p>
                <span className="text-[10px] text-txt-tertiary/70 shrink-0">{fmtTime(it.ts)}</span>
              </div>
            ) : (
              <div className="rounded-xl border border-border bg-surface px-3.5 py-2.5 flex gap-3 cursor-default"
                onClick={it.kind === 'email' ? openEmail : it.kind === 'meeting' ? openMeetings : undefined}
                style={it.kind === 'email' || it.kind === 'meeting' ? { cursor: 'pointer' } : undefined}>
                <span className={`w-7 h-7 rounded-full border flex items-center justify-center shrink-0 ${KIND_ICON[it.kind]?.cls || 'bg-surface-tertiary text-txt-secondary border-border'}`}>
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round"><path d={KIND_ICON[it.kind]?.d || ''} /></svg>
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <p className="text-[12.5px] font-bold text-txt-primary truncate flex-1">
                      {it.kind === 'email' ? <>{it.meta === 'in' ? 'Email received' : 'Email sent'} — <span className="font-semibold">{it.title}</span></> : it.title}
                    </p>
                    <span className="text-[10.5px] text-txt-tertiary shrink-0">{it.who ? `${it.who} · ` : ''}{fmtTime(it.ts)}</span>
                  </div>
                  {it.body && <p className="text-[11.5px] text-txt-secondary truncate">{it.body}</p>}
                  {it.kind === 'meeting' && it.booking && (
                    <p className="text-[11px] text-txt-tertiary">
                      {it.booking.status}{it.booking.recordingUrl ? ' · ▶ recording' : ''}{it.booking.transcriptUrl ? ' · transcript' : ''} · open Meetings →
                    </p>
                  )}
                </div>
              </div>
            )}
          </React.Fragment>
        )
      })}
    </div>
  )
}

/* ── Tasks chip panel (same data as the old Tasks tab) ── */
const TasksPanel: React.FC<{ deal: Deal & Record<string, any>; onChanged: () => void }> = ({ deal, onChanged }) => {
  const [title, setTitle] = useState('')
  const tasks: Task[] = (deal.tasks || []) as Task[]
  const add = () => {
    const v = title.trim(); if (!v) return
    addTask(deal.id, { title: v, dueInDays: 1 }).then(() => { setTitle(''); crmToast('Task added', 'success'); onChanged() }).catch(() => crmToast('Not saved', 'error'))
  }
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <input value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} placeholder="New task…"
          className="flex-1 rounded-lg border border-border bg-surface px-3 py-1.5 text-[12px] focus:outline-none focus:border-brand-400" />
        <Button size="sm" onClick={add}>Add</Button>
      </div>
      {tasks.filter((t) => !t.done).map((t) => (
        <label key={t.id} className="flex items-center gap-2.5 rounded-lg border border-border bg-surface px-3 py-2 cursor-pointer">
          <input type="checkbox" checked={false} onChange={() => updateTask(t.id, { done: true }).then(() => { crmToast('Done ✓', 'success'); onChanged() })} className="w-4 h-4 accent-emerald-600" />
          <span className="text-[12.5px] text-txt-primary flex-1">{t.title}{t.isNext && <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] font-bold bg-brand-50 text-brand-700">NEXT</span>}</span>
          <span className={`text-[11px] ${t.due != null && t.due < 0 ? 'text-red-600 font-bold' : 'text-txt-tertiary'}`}>{t.due == null ? '' : t.due < 0 ? `${-t.due}d overdue` : t.due === 0 ? 'today' : `in ${t.due}d`}</span>
          {!t.isNext && <button onClick={(e) => { e.preventDefault(); updateTask(t.id, { isNext: true }).then(onChanged) }} className="text-[10px] font-bold text-brand-600 hover:underline">make next</button>}
        </label>
      ))}
      {tasks.filter((t) => t.done).length > 0 && (
        <details className="pt-1"><summary className="text-[11px] text-txt-tertiary cursor-pointer">{tasks.filter((t) => t.done).length} completed</summary>
          {tasks.filter((t) => t.done).map((t) => <p key={t.id} className="text-[11.5px] text-txt-tertiary line-through pl-1 py-0.5">{t.title}</p>)}
        </details>
      )}
      {!tasks.length && <p className="text-[12px] text-txt-tertiary">No tasks yet.</p>}
    </div>
  )
}

export default LeadScreen
